import { useEffect, useState, useRef, useContext } from "react";
import { Alert, Col } from "react-bootstrap";
import { Link } from 'react-router-dom';
import AuthContext from '../AuthContextComponent/AuthContext';
import WarningMessagePopup from "../PopupComponent/WarningMessagePopup";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable, } from '@tanstack/react-table';
import { ReactComponent as CheckBoxUnselectedHover } from '../../Assets/img/CheckBoxUnselectedHover.svg';
import { ReactComponent as CheckBoxPartial } from '../../Assets/img/CheckBoxPartial.svg';
import { ReactComponent as CheckBoxPartialHover } from '../../Assets/img/CheckBoxPartialHover.svg';
import { ReactComponent as CheckBoxUnselected } from '../../Assets/img/CheckBoxUnselected.svg';
import { ReactComponent as CheckBoxSelected } from '../../Assets/img/CheckBoxSelected.svg';
import { ReactComponent as CheckBoxSelectedHover } from '../../Assets/img/CheckBoxSelectedHover.svg';
import { ReactComponent as DescendingIcon } from '../../Assets/img/DescendingIcon.svg';
import { ReactComponent as AscendingIcon } from '../../Assets/img/AscendingIcon.svg';
import { ReactComponent as DescendingHoverIcon } from '../../Assets/img/DescendingHoverIcon.svg';
import { ReactComponent as AscendingHoverIcon } from '../../Assets/img/AscendingHoverIcon.svg';
import { ReactComponent as Edit } from '../../Assets/img/EditPen.svg';
import { ReactComponent as EditPenHover } from '../../Assets/img/EditPenHover.svg';
import { ReactComponent as ResetPasswordIcon } from '../../Assets/img/Reset.svg';
import { ReactComponent as Show } from '../../Assets/img/Show.svg';
import { ReactComponent as Hide } from '../../Assets/img/Hide.svg';
import PageTop from "../PageComponent/PageTop";
import { CSVLink } from "react-csv";
import TextFilterBox from "../PopupComponent/TextFilterBox";
import UserService from "../../Services/UserService";
import LoadingIndicator from "../CommonComponent/LoadingIndicator";
import NoMatch from "../CommonComponent/NoMatch";
import Footer from "../FooterComponent/Footer";
import "bootstrap/dist/css/bootstrap.min.css";
import MessagePopup from "../PopupComponent/MessagePopup";
import Navigation from "../NavigationComponent/Navigation";
import TopNavigation from "../NavigationComponent/TopNavigation";
import PageTable from "../PageComponent/PageTable";
import AddUser from "../UserComponent/AddUser";
import EditUser from "./EditUser";
import ResetPassword from "./ResetPassword";
import AddRole from "./AddRole";

const columnHelper = createColumnHelper();
const columns = [columnHelper.accessor('User Name', {
    cell: info => <span style={{ width: info.column.getSize() }}>{info.getValue()}</span>,
    header: info => <span style={{ width: info.column.getSize() }}>User Name</span>,
    id: "Username",
    meta: {
        sortFieldName: "Username",
        type: "string"
    },
    size: 300
}), columnHelper.accessor('Role', {
    cell: info => <span style={{ width: info.column.getSize() }}>{info.getValue()}</span>,
    header: info => <span style={{ width: info.column.getSize() }}>Role</span>,
    id: "Role",
    meta: {
        sortFieldName: "RoleName",
        type: "string"
    },
    size: 300
}), columnHelper.accessor('Email', {
    cell: info => <span style={{ width: info.column.getSize() }}>{info.getValue()}</span>,
    header: info => <span style={{ width: info.column.getSize() }}>Email</span>,
    id: "Email",
    meta: {
        sortFieldName: "Email",
        type: "string"
    },
    size: 300
})];

/**
 * Renders user page with all the user functions
 * @component
 */
function User() {

    // Used to hide the Reset Password action on the signed-in admin's own row -- resetting your
    // own password is rejected by the server; Change Password is the path for that.
    const authCtx = useContext(AuthContext);

    //#region States

    const [state, setState] = useState({
        status: 'LOADING',
        userList: null,
        user: "",
        popup: false,
        message: null,
        error: null,
        sortField: null,
        sortType: null,
        selectedItems: [],
        showFilter: false,
        allColumnFilter: null,
        userNameFilter: null,
        searchUserName: "",
        roleFilter: null,
        searchRole: "",
        emailFilter: null,
        searchEmail: "",
        isTableLoading: false,
        model: null,
        perPage: 20,
        currentPage: 0,
        pageCount: 0,
        totalDataCount: 0,
        selectedItemsForPopup: [],
        hiddenColumns: [],
        allUsers: [],
        triggerCSVDownload: false
    });

    //#endregion

    //#region Refs

    const searchValue = useRef();
    const csvRef = useRef();

    const [data, setData] = useState([]);

    const table = useReactTable({
        data,
        columns,
        columnResizeMode: "onChange",
        getCoreRowModel: getCoreRowModel(),
    });
    //#endregion

    //#region CSV headers

    const csvHeader = [
        { label: "User Name", key: "username" },
        { label: "Role", key: "role" },
        { label: "Email", key: "email" }
    ];

    //#endregion

    //#region useEffect hook

    /**
   * Calls loadUserList(); method to get user details
   * Runs on state change of state.sortField, state.sortType, state.allColumnFilter, state.perPage, state.currentPage, state.userNameFilter, state.roleFilter, state.emailFilter
   */
    useEffect(() => {
        async function fetchData() {
            await loadUserList();
        }
        fetchData();
    }, [state.sortField, state.sortType, state.allColumnFilter, state.perPage, state.currentPage, state.userNameFilter, state.roleFilter, state.emailFilter]);

    /**
   * Calls loadUserList() method to get user details on successfull execution of user service methods
   * Runs only on state change of state.success
   */
    useEffect(() => {
        async function fetchData() {
            await loadUserList();
        }
        if (state.success == true) {
            fetchData();
        }
    }, [state.success]);

    useEffect(() => {
        if (state.triggerCSVDownload) {
            csvRef.current.link.click();
            setState((currentState) => ({
                ...currentState,
                triggerCSVDownload: false
            }));
        }
    }, [state.triggerCSVDownload])

    //#endregion

    //#region Methods

    /**
   * Calls user service to fetch users according to user filters
   */
    const loadUserList = async () => {
        setState((currentState) => ({
            ...currentState,
            isTableLoading: true,
        }));

        try {
            var request = {
                Filters: [state.allColumnFilter, state.userNameFilter, state.roleFilter, state.emailFilter].filter(f => f != null),
                Sort: (state.sortField == null || state.sortType == null) ? [] : [{
                    SortField: state.sortField,
                    sortType: state.sortType
                }],
                Pagination: {
                    PageStart: state.currentPage,
                    PageFetch: state.perPage
                }
            }

            const response = await UserService.GetUsersPerPage(request);

            var responseD = [];
            response.data.forEach((users) => {
                responseD.push({
                    "User Name": users.username,
                    "Role": users.role,
                    "Email": users.email,
                })
            })
            setData(responseD);

            console.log(response);

            setState((currentState) => ({
                ...currentState,
                isTableLoading: false,
                status: 'LOADED',
                userList: response.data,
                pageCount: Math.ceil(
                    response.totalRecord / state.perPage
                ),
                totalDataCount: response.totalRecord
            }));
        } catch (error) {
            setState((currentState) => ({
                ...currentState,
                status: 'ERROR',
                error: error
            }));
        }
    }

    /**
   * Calls user service to fetch all registered users
   */
    const getAllUsers = async () => {
        UserService.getUsers().then((users) => {
            var _users = [];
            users.forEach((user) => {
                _users.push({
                    "username": user.userName,
                    "role": user.roles[0],
                    "email": user.email
                })
            })
            setState((currentState) => ({
                ...currentState,
                allUsers: _users,
                triggerCSVDownload: true
            }));
        }).catch((error) => {
            setState((currentState) => ({
                ...currentState,
                status: "ERROR",
                error: error,
            }));
        });
    }

    /**
     * Move to next page
     */
    const nextPageHandler = () => {
        if (state.currentPage + 2 > state.pageCount) {
            return;
        }
        setState((currentState) => ({
            ...currentState,
            currentPage: currentState.currentPage + 1,
            selectedItems: []
        }));
    }

    /**
    * Move to previous page
    */
    const previousPageHandler = () => {
        if (state.currentPage - 1 < 0) {
            return;
        }
        setState((currentState) => ({
            ...currentState,
            currentPage: currentState.currentPage - 1,
            selectedItems: []
        }));
    }

    /**
    * Move to first page
    */
    const firstPageHandler = () => {
        if (state.currentPage == 0) {
            return;
        }
        setState((currentState) => ({
            ...currentState,
            currentPage: 0,
            selectedItems: []
        }));
    }

    /**
    * Move to last page
    */
    const lastPageHandler = () => {
        if (state.currentPage == state.pageCount - 1) {
            return;
        }
        setState((currentState) => ({
            ...currentState,
            currentPage: currentState.pageCount - 1,
            selectedItems: []
        }))
    }

    /**
    * Sort the given field  ("asc" to "desc", "desc" to "asc", null to "asc") 
    * @param   {String} field  Coloumn name that user wants to change 
    */
    const sort = (field) => {
        var type = state.sortType;
        var fieldValue = state.sortField;
        if (type == null) {
            type = "asc";
        }
        else if (type == "asc") {
            if (fieldValue == field) {
                type = "desc";
            }
            else {
                type = "asc";
            }
        }
        else {
            if (fieldValue == field) {
                type = null;
            }
            else {
                type = "asc";
            }
        }

        setState((currentState) => ({
            ...currentState,
            sortField: field,
            sortType: type,
            selectedItems: []
        }));
    }

    /**
   * Push the user into the list of selected users 
   * @param   {Object} user  Object that contains UserName,Role,Email
   */
    const selectItem = (user) => {
        var selected = state.selectedItems;
        selected.push(user);
        setState((currentState) => ({
            ...currentState,
            selectedItems: selected
        }));
    }

    /**
  * Remove the user from the list of selected users
  * @param   {Object} user  Object that contains UserName,Role,Email
  */
    const unSelectItem = (user) => {
        var selected = state.selectedItems.filter(sel => sel != user);
        setState((currentState) => ({
            ...currentState,
            selectedItems: selected
        }));
    }

    /**
   * Push all the users into the list of selected users 
   */
    const selectAllItems = () => {
        var selected = [];
        state.userList.forEach(user => {
            // Neither the system administrator account nor your own can be deleted, so never select them.
            if (!user.isSystemUser && user.username !== authCtx.selectedUserName) {
                selected.push(user);
            }
        });
        setState((currentState) => ({
            ...currentState,
            selectedItems: selected
        }));
    }

    /**
   * Remove all the users from the list of selected users 
   */
    const unSelectAllItems = () => {
        setState((currentState) => ({
            ...currentState,
            selectedItems: []
        }));
    }

    /**
  * Changes the number of users per page according to user input
  * @param    {Event} event  User action (on changing value)
  */
    const changePerPage = (event) => {
        setState((currentState) => ({
            ...currentState,
            perPage: event.target.value,
            currentPage: 0,
            selectedItems: []
        }));
    }

    /**
   * Search user 
   */
    const searchAllColumn = () => {
        if (searchValue.current.value == "") {
            setState((currentState) => ({
                ...currentState,
                allColumnFilter: null
            }));
        }
        else {
            setState((currentState) => ({
                ...currentState,
                allColumnFilter: {
                    Type: "contains",
                    Value: searchValue.current.value,
                    ColumnNames: ["username", "rolename", "email"]
                }
            }));
        }
    }

    /**
    * Search user by user name
    * @param    {Event} event  User action (on changing value)
    */
    const searchByUserName = (event) => {
        setState((currentState) => ({
            ...currentState,
            searchUserName: event.target.value
        }));

        var filter;
        if (event.target.value == "") {
            filter = null;
        }
        else {
            filter = {
                ColumnNames: ["username"],
                Type: "contains",
                Value: event.target.value
            };
        }
        setState((currentState) => ({
            ...currentState,
            userNameFilter: filter
        }));
    }

    /**
    * Search user by role name
    * @param    {Event} event  User action (on changing value)
    */
    const searchByRole = (event) => {
        setState((currentState) => ({
            ...currentState,
            searchRole: event.target.value
        }));

        var filter;
        if (event.target.value == "") {
            filter = null;
        }
        else {
            filter = {
                ColumnNames: ["rolename"],
                Type: "contains",
                Value: event.target.value
            };
        }
        setState((currentState) => ({
            ...currentState,
            roleFilter: filter
        }));
    }

    /**
    * Search user by email
    * @param    {Event} event  User action (on changing value)
    */
    const searchByEmail = (event) => {
        setState((currentState) => ({
            ...currentState,
            searchEmail: event.target.value
        }));

        var filter;
        if (event.target.value == "") {
            filter = null;
        }
        else {
            filter = {
                ColumnNames: ["email"],
                Type: "contains",
                Value: event.target.value
            };
        }
        setState((currentState) => ({
            ...currentState,
            emailFilter: filter
        }));
    }

    /**
    * Clear all the applied filters
    */
    const clearFilters = () => {
        setState((currentState) => ({
            ...currentState,
            userNameFilter: null,
            searchUserName: "",
            roleFilter: null,
            searchRole: "",
            emailFilter: null,
            searchEmail: "",
        }))
    }

    /**
   * Reset the page to default
   */
    const resetPage = () => {
        setState((currentState) => ({
            ...currentState,
            status: "LOADED",
            error: null,
            perPage: 20,
            currentPage: 0,
            sortField: null,
            sortType: null,
            selectedItems: [],
            showFilter: false,
            allColumnFilter: null,
            userNameFilter: null,
            searchUserName: "",
            roleFilter: null,
            searchRole: "",
            emailFilter: null,
            searchEmail: "",
            isTableLoading: false,
            model: null,
            success: null,
            message: null,
            hiddenColumns: [],
            allUsers: [],
            selectedItemsForPopup: []
        }));
        searchValue.current.value = ""
        table.getHeaderGroups().map((headerGroup) => (
            headerGroup.headers.map((header) => (
                header.column.resetSize()
            ))
        ))
    }

    /**
    * Show the selected column in users table
    * @param    {String} columnName  Column name that user selected to show
    */
    const showColumn = (columnName) => {
        if (columnName == "") {
            setState((currentState) => ({
                ...currentState,
                hiddenColumns: []
            }));
        }

        var columns = state.hiddenColumns.filter(c => c != columnName);

        setState((currentState) => ({
            ...currentState,
            hiddenColumns: columns
        }));
    }

    /**
    * Hide the selected column from users table
    * @param    {String} columnName  Column name that user selected to hide
    */
    const hideColumn = (columnName) => {
        var columns = state.hiddenColumns;
        columns.push(columnName);

        setState((currentState) => ({
            ...currentState,
            hiddenColumns: columns
        }));
    }

    /**
    * Show all the columns in users table
     */
    const showAllColumns = () => {
        setState((currentState) => ({
            ...currentState,
            hiddenColumns: []
        }));
    }

    /**
  * Deletes all the selected users
  */
    const DeleteUser = async () => {
        try {
            var Ids = [];
            state.selectedItems.forEach(u => Ids.push(u.username));
            var request = {
                Ids: Ids
            }
            await UserService.BulkUserDelete(request);

            setState((currentState) => ({
                ...currentState,
                selectedItems: [],
                model: null,
                success: true,
                message: "Users deleted",
            }));
        } catch (error) {
            setState((currentState) => ({
                ...currentState,
                status: "ERROR",
                error: error,

            }));
        }
    };

    /**
   * Opens model
   * @param    {String} model  Name of model to open
   * @param    {String} param  Value of parameter corresponding to model
   */
    const modelOpenHandler = (model, param) => {
        if (model == "edit" || model == "resetPassword") {
            setState((currentState) => ({
                ...currentState,
                user: param
            }));
        }

        setState((currentState) => ({
            ...currentState,
            model: model
        }));
    }

    /**
  * Closes message popup
  */
    const messagePopupCloseHandler = () => {
        setState((currentState) => ({
            ...currentState,
            selectedItems: [],
            selectedItemsForPopup: [],
            success: null,
            message: null
        }));
    }

    /**
   * Display message
   * @param    {String} message  Message needs to be displayed
   */
    const messageDisplayHandler = async (message) => {
        setState((currentState) => ({
            ...currentState,
            selectedItems: [],
            selectedItemsForPopup: [],
            success: true,
            message: message
        }));
    }

    /**
   * Closes opened model
   */
    const modelCloseHandler = async () => {
        setState((currentState) => ({
            ...currentState,
            model: null
        }));
    }

    //#endregion

    return (
        <>
            {state.status == "LOADING" && <LoadingIndicator size="large" message="Fetching..." />}
            {state.status == "ERROR" && (state.error?.message?.indexOf("404") > -1 ? <NoMatch /> : <Alert variant="danger">{state.error?.message}</Alert>)}
            {state.status == "LOADED" &&
                <>
                    <Navigation />
                    <TopNavigation onPageReset={resetPage} />
                    <div className="page-container">
                        {state.success && <MessagePopup onClose={messagePopupCloseHandler} title="Success" body={<div className="message-popup-container-body"> {state.message}
                            {state.selectedItemsForPopup.length != 0 &&
                                <table className="popup-table">
                                    <tr>
                                        <th className="warning-message-popup-first">UserName</th>
                                        <th className="warning-message-popup-last">Role</th>
                                        <th className="warning-message-popup-last">Email</th>
                                    </tr>
                                    <tbody className="popup-tbody">{state.selectedItemsForPopup?.slice(0, 3).map(userDetails => (
                                        <tr>
                                            <td className="warning-message-popup-first">
                                                {userDetails.UserName}
                                            </td>
                                            <td className="warning-message-popup-last">
                                                {userDetails.Role}
                                            </td>
                                            <td className="warning-message-popup-last">
                                                {userDetails.Email}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                    <div className="bottom-title">
                                        {state.selectedItemsForPopup?.length > 3 && <p>... and {state.selectedItemsForPopup.length - 3} more {state.selectedItemsForPopup.length - 3 == 1 ? 'record' : 'records'}. </p>}
                                    </div>
                                </table>}
                        </div>} />}
                        {state.model == "add" && <AddUser onShowMessage={messageDisplayHandler} onClose={modelCloseHandler} action="Yes" />}
                        {state.model == "edit" && <EditUser onShowMessage={messageDisplayHandler} onClose={modelCloseHandler} action="Yes" user={state.user} />}
                        {state.model == "resetPassword" && <ResetPassword onShowMessage={messageDisplayHandler} onClose={modelCloseHandler} action="Yes" user={state.user} />}
                        {state.model == "create" && <AddRole onShowMessage={messageDisplayHandler} onClose={modelCloseHandler} action="Yes" />}
                        {state.model == "delete" && <WarningMessagePopup onClose={modelCloseHandler} title="Delete User" body={<div className="message-popup-container-body"> Do you want to Delete :
                            {state.selectedItems != null &&
                                <table className="popup-table">
                                    <tr>
                                        <th className="warning-message-popup-first">UserName</th>
                                        <th className="warning-message-popup-last">Role</th>
                                    </tr>
                                    <tbody className="popup-tbody">{state.selectedItems?.slice(0, 3).map(userList => (
                                        <tr>
                                            <td className="warning-message-popup-first">
                                                {userList.username}
                                            </td>
                                            <td className="warning-message-popup-last">
                                                {userList.role}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                    <div className="bottom-title">
                                        {state.selectedItems?.length > 3 && <p>... and {state.selectedItems.length - 3} more {state.selectedItems.length - 3 == 1 ? 'record' : 'records'} ? </p>}
                                    </div>
                                </table>}
                        </div>} action="Yes" onAction={DeleteUser} />}
                        <PageTop searchPlaceholder="Search User"
                            addButtonText="Add User"
                            secondAddButtonText="Add Role"
                            disableSecondAddButton
                            disableUploadButton
                            searchRef={searchValue}
                            showFilter={state.showFilter}
                            searchAllColumnHandler={searchAllColumn}
                            resetPageHandler={resetPage}
                            filterColumnHandler={() => {
                                setState((currentState) => ({ ...currentState, showFilter: !currentState.showFilter }));
                                if (state.showFilter) {
                                    clearFilters();
                                }
                            }}
                            modelOpenHandler={modelOpenHandler}
                            menuData={<div className="menu-popup-container">
                                <span className="bold-underline-text" onClick={() => {
                                    showAllColumns();
                                }}>
                                    ShowAll
                                </span>
                                <div className="columns-container">
                                    {table.getHeaderGroups().map(headerGroup => (
                                        headerGroup.headers.map(header => (
                                            <div className="column-container">
                                                <span className="column-text">{header.column.columnDef.accessorKey}</span>
                                                <span className="column-icon">
                                                    {state.hiddenColumns?.includes(header.id) ?
                                                        <Hide onClick={() => {
                                                            showColumn(header.id);
                                                        }} />
                                                        : <Show style={{ opacity: 0.5 }} onClick={() => {
                                                            hideColumn(header.id);
                                                        }} />}
                                                </span>
                                            </div>
                                        ))
                                    ))}
                                </div>
                                <div style={{ margin: "0 8px 10px 8px", borderTop: "1px solid #ddd" }}></div>
                                <div className="columns-container">
                                    <div className="bottom-column-container">
                                        {state.userList.length === 0 ?
                                            (<span className="bottom-column-text" style={{ opacity: 0.5 }}>
                                                Export All...
                                            </span>) :
                                            (<>
                                                <span className="bottom-column-text" onClick={getAllUsers} style={{ cursor: "pointer", maxWidth: "fit-content" }}>Export All...</span>
                                                <CSVLink
                                                    data={state.allUsers}
                                                    headers={csvHeader}
                                                    separator={","}
                                                    filename={"Users.csv"}
                                                    asyncOnClick={true}
                                                    ref={csvRef} target="_blank" ></CSVLink>
                                            </>)}
                                    </div>
                                    <div className="bottom-column-container">
                                        {state.selectedItems.length === 0 ?
                                            (<span className="bottom-column-text" style={{ opacity: 0.5 }}>
                                                Export Selected...
                                            </span>) :
                                            (<span>
                                                <CSVLink className="bottom-column-text"
                                                    data={state.selectedItems}
                                                    headers={csvHeader}
                                                    separator={","}
                                                    filename={"Users.csv"}>
                                                    Export Selected...
                                                </CSVLink>
                                            </span>)}
                                    </div>
                                </div>
                            </div>} />
                        <PageTable isTableLoading={state.isTableLoading}
                            tableData={
                                <table>
                                    <thead className="table-header">
                                        <tr class="normal-header">
                                            <th scope="col" style={{ padding: "0 5px 0 5px" }}>
                                                {state.selectedItems.length == 0 ?
                                                    <Col className="menu-icon-container">
                                                        <CheckBoxUnselected onClick={selectAllItems} />
                                                        <CheckBoxUnselectedHover className="hover-icon" onClick={selectAllItems} />
                                                    </Col>
                                                    :
                                                    (state.selectedItems.length == state.userList.length ?
                                                        <Col className="menu-icon-container">
                                                            <CheckBoxSelected onClick={unSelectAllItems} />
                                                            <CheckBoxSelectedHover className="hover-icon" onClick={unSelectAllItems} />
                                                        </Col> :
                                                        <Col className="menu-icon-container">
                                                            <CheckBoxPartial onClick={selectAllItems} />
                                                            <CheckBoxPartialHover className="hover-icon" onClick={selectAllItems} />
                                                        </Col>)}
                                            </th>
                                            {table.getHeaderGroups().map(headerGroup => (
                                                headerGroup.headers.map(header => (
                                                    !state.hiddenColumns.includes(header.id) &&
                                                    <th key={header.id} scope="col"
                                                        style={{ width: header.getSize(), cursor: "pointer", textAlign: header.column.columnDef.meta.type == "date" ? "right" : (header.column.columnDef.meta.type == "bool" ? "center" : "left") }}>
                                                        <div className="header-text-wrapper" onClick={() => { sort(header.column.columnDef.meta.sortFieldName) }}>
                                                            {state.sortField == header.column.columnDef.meta.sortFieldName && state.sortType == "asc" ?
                                                                <div className={`${header.column.columnDef.meta.type == "date" ? 'sort-icon-left' : 'sort-icon-right'}`}>
                                                                    <DescendingIcon />
                                                                    <AscendingHoverIcon />
                                                                </div> : (state.sortField == header.column.columnDef.meta.sortFieldName && state.sortType == "desc" ?
                                                                    <div className={`${header.column.columnDef.meta.type == "date" ? 'sort-icon-left' : 'sort-icon-right'}`} >
                                                                        <DescendingHoverIcon />
                                                                        <AscendingIcon />
                                                                    </div> : <div className={`${header.column.columnDef.meta.type == "date" ? 'sort-icon-left' : 'sort-icon-right'}`} >
                                                                        <DescendingIcon />
                                                                        <AscendingIcon />
                                                                    </div>)}
                                                            {header.isPlaceholder
                                                                ? null
                                                                : flexRender(
                                                                    header.column.columnDef.header,
                                                                    header.getContext()
                                                                )}
                                                        </div>
                                                        <div className={`resize-handle ${header.column.getIsResizing() ? 'active' : null}`} onMouseDown={header.getResizeHandler()} onTouchStart={header.getResizeHandler()}></div>
                                                    </th>
                                                ))
                                            ))}
                                            <th scope="col"></th>
                                            <th scope="col" style={{ width: "80px", textAlign: "center" }}>Password</th>
                                        </tr>
                                        {state.showFilter &&
                                            <tr class="filter-header">
                                                <th scope="col"></th>
                                                {!state.hiddenColumns.includes("UserName") &&
                                                    <th scope="col" style={{ padding: "0 5px 0 5px" }} >
                                                        <TextFilterBox clearFilterHandler={() => {
                                                            setState((currentState) => ({
                                                                ...currentState,
                                                                searchUserName: "", userNameFilter: null,
                                                            }));
                                                        }}
                                                            searchPlaceHolder={state.searchUserName}
                                                            searchHandler={searchByUserName} />
                                                    </th>}
                                                {!state.hiddenColumns.includes("Role") &&
                                                    <th scope="col" style={{ padding: "0 5px 0 5px" }} >
                                                        <TextFilterBox clearFilterHandler={() => {
                                                            setState((currentState) => ({
                                                                ...currentState,
                                                                searchRole: "", roleFilter: null,
                                                            }));
                                                        }}
                                                            searchPlaceHolder={state.searchRole}
                                                            searchHandler={searchByRole} />
                                                    </th>}
                                                {!state.hiddenColumns.includes("Email") &&
                                                    <th scope="col" style={{ padding: "0 5px 0 5px" }} >
                                                        <TextFilterBox clearFilterHandler={() => {
                                                            setState((currentState) => ({
                                                                ...currentState,
                                                                searchEmail: "", emailFilter: null,
                                                            }));
                                                        }}
                                                            searchPlaceHolder={state.searchEmail}
                                                            searchHandler={searchByEmail} />
                                                    </th>}

                                                <th scope="col"></th>
                                                <th scope="col"></th>
                                            </tr >
                                        }
                                    </thead>
                                    {!state.isTableLoading &&
                                        <tbody >
                                            {table.getRowModel().rows.map(row => (
                                                <tr key={row.id} className={state.selectedItems.find(sel => sel.username == state.userList[row.id].username && sel.Role == state.userList[row.id].Role) == null ? '' : 'selected'}>
                                                    <td scope="col">
                                                        {/* Neither the system administrator account nor your own can be deleted, so
                                                            neither can be selected. The backend rejects both regardless -- this just
                                                            avoids a pointless failure. */}
                                                        {(state.userList[row.id].isSystemUser || state.userList[row.id].username === authCtx.selectedUserName) ?
                                                            <Col className="menu-icon-container">
                                                                <CheckBoxUnselected style={{ opacity: 0.3, cursor: "not-allowed" }}
                                                                    title={state.userList[row.id].isSystemUser
                                                                        ? "The system administrator account cannot be deleted"
                                                                        : "You cannot delete your own user account"} />
                                                            </Col>
                                                            :
                                                            (state.selectedItems.find(sel => sel.username == state.userList[row.id].username && sel.Role == state.userList[row.id].Role) == null ?
                                                                <Col className="menu-icon-container">
                                                                    <CheckBoxUnselected onClick={() => { selectItem(state.userList[row.id]) }} />
                                                                    <CheckBoxUnselectedHover className="hover-icon" onClick={() => { selectItem(state.userList[row.id]) }} />
                                                                </Col>
                                                                :
                                                                <Col className="menu-icon-container">
                                                                    <CheckBoxSelected onClick={() => { unSelectItem(state.userList[row.id]) }} />
                                                                    <CheckBoxSelectedHover className="hover-icon" onClick={() => { unSelectItem(state.userList[row.id]) }} />
                                                                </Col>)
                                                        }
                                                    </td>
                                                    {row.getVisibleCells().map(cell => (
                                                        !state.hiddenColumns.includes(cell.column.id) &&
                                                        (cell.column.columnDef.meta.type == "date" ?
                                                            <td scope="col" key={cell.id} style={{ textAlign: "right" }}>
                                                                <div className="body-text-wrapper">
                                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                </div>
                                                            </td> :
                                                            <td scope="col" key={cell.id}>
                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                            </td>)
                                                    )
                                                    )}
                                                    <td scope="col">
                                                        <span className="menu-icon-container">
                                                            <Edit className="menu-icon" onClick={() => modelOpenHandler("edit", state.userList[row.id])} />
                                                            <EditPenHover className="menu-icon hover-icon" onClick={() => modelOpenHandler("edit", state.userList[row.id])} />
                                                        </span>
                                                    </td>
                                                    <td scope="col" style={{ width: "80px", textAlign: "center" }}>
                                                        <span className="menu-icon-container">
                                                            {state.userList[row.id].username === authCtx.selectedUserName ?
                                                                <ResetPasswordIcon className="menu-icon" style={{ opacity: 0.3, cursor: "not-allowed" }} title="Use Change Password to change your own password" />
                                                                :
                                                                <ResetPasswordIcon className="menu-icon" title="Reset Password" onClick={() => modelOpenHandler("resetPassword", state.userList[row.id])} />}
                                                        </span>
                                                    </td>
                                                </tr>

                                            ))}
                                        </tbody>}
                                </table>
                            } />
                        <Footer onFirstClick={firstPageHandler}
                            onLastClick={lastPageHandler}
                            onPreviousClick={previousPageHandler}
                            onNextClick={nextPageHandler}
                            perPage={state.perPage}
                            totalData={state.totalDataCount}
                            showingItemsCount={state.userList.length}
                            currentPage={state.currentPage + 1}
                            totalPage={state.pageCount}
                            selectedItems={state.selectedItems.length}
                            changePerPage={changePerPage}
                            modelOpenHandler={modelOpenHandler}
                            disableEditButton
                            disableLine
                        />
                    </div>
                </>
            }
        </>
    )
}

export default User;